import { useEffect, useState } from 'react';
import { useAuth } from '@hooks/useAuth';
import { getAssetsAccess } from './services/asset.service';

/**
 * Is this user allowed into the Assets module (inventory, allocation, offboarding IT
 * clearance)?
 *
 * The rule lives in the API's AssetsAccess policy — the tenant's IT department at Admin or
 * Director level — and is resolved there from the employee record, so the answer has to be
 * asked for rather than derived from the JWT. It also means the nav and the API can never
 * disagree about who gets in.
 *
 * Returns null while the answer is still in flight, so callers can wait instead of flashing
 * the menu entry and pulling it back.
 */
let cache: { userId: string; answer: Promise<boolean> } | null = null;

export function useAssetsAccess(): boolean | null {
  const { user } = useAuth();
  const userId = user?.id;
  const [allowed, setAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    if (!userId) {
      setAllowed(false);
      return;
    }

    // One request per session, but keyed by user — signing in as someone else in the same
    // tab must not inherit the previous user's answer.
    if (cache?.userId !== userId) {
      cache = { userId, answer: getAssetsAccess() };
    }
    const pending = cache.answer;

    let active = true;
    pending
      .then(v => {
        if (active) setAllowed(v);
      })
      .catch(() => {
        if (active) setAllowed(false);
      });

    return () => {
      active = false;
    };
  }, [userId]);

  return allowed;
}
